import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type RefObject,
} from 'react'
import type { EmployeeGoldFactMonthRow } from '../types/employeeAnalysis'
import {
  buildEmployeeMonthlyChartData,
  computeEmployeeUserStats,
  getMonthlyDetailsExportRows,
  type ComputedEmployeeUserStats,
} from '../utils/computeEmployeeUserStats'
import {
  buildFYOptions,
  getCurrentFYStartYear,
  type PeriodFilter,
} from '../utils/projectAnalysisPeriod'
import {
  exportRowsToCsv,
  exportRowsToExcel,
  getProjectsExportRows,
} from '../utils/employeeAnalysisExport'

export type EmployeeDetailTab =
  | 'overview'
  | 'trends'
  | 'projects'
  | 'details'

export interface UseEmployeeDetailPageArgs {
  userId: number
  data: EmployeeGoldFactMonthRow[]
  loading: boolean
  error: string | null
  onBack: () => void
}

export interface EmployeeDetailPageModel {
  userId: number
  onBack: () => void
  loading: boolean
  error: string | null
  rawData: EmployeeGoldFactMonthRow[]
  userStats: ComputedEmployeeUserStats | null
  headerTitle: string
  headerEmail: string | null
  headerUsername: string | null
  headerStatusActive: boolean
  detailMode: 'fy' | 'calendar'
  setDetailMode: (m: 'fy' | 'calendar') => void
  detailFY: string
  setDetailFY: (v: string) => void
  detailQ: string
  setDetailQ: (v: string) => void
  detailYear: string
  setDetailYear: (v: string) => void
  detailMonth: string
  setDetailMonth: (v: string) => void
  profileFYOptions: { value: string; label: string }[]
  profileYearOptions: number[]
  activeTab: EmployeeDetailTab
  setActiveTab: (t: EmployeeDetailTab) => void
  selectedProjectId: number | null
  setSelectedProjectId: (id: number | null) => void
  detailsYearFilter: string
  setDetailsYearFilter: (v: string) => void
  detailsMonthFilter: string
  setDetailsMonthFilter: (v: string) => void
  breakdownSectionRef: RefObject<HTMLDivElement | null>
  selectedProjectRowRef: RefObject<HTMLTableRowElement | null>
  monthlyChartData: ReturnType<typeof buildEmployeeMonthlyChartData>
  pieChartData: { name: string; value: number; color: string }[]
  chartAxis: {
    grid: string
    tick: string
    tooltipBg: string
    tooltipBorder: string
  }
  exportDetailsRows: Record<string, string | number>[]
  gidLate: string
  gidUtil: string
  onExportProjectsCsv: () => void
  onExportProjectsExcel: () => void
  onExportDetailsCsv: () => void
  onExportDetailsExcel: () => void
}

export function useEmployeeDetailPage({
  userId,
  data,
  loading,
  error,
  onBack,
}: UseEmployeeDetailPageArgs): EmployeeDetailPageModel {
  const gidLate = useId().replace(/:/g, '')
  const gidUtil = useId().replace(/:/g, '')

  const [detailMode, setDetailMode] = useState<'fy' | 'calendar'>('fy')
  const [detailFY, setDetailFY] = useState('2025-26')
  const [detailQ, setDetailQ] = useState('all')
  const [detailYear, setDetailYear] = useState('all')
  const [detailMonth, setDetailMonth] = useState('all')
  const [activeTab, setActiveTab] = useState<EmployeeDetailTab>('overview')
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    null,
  )
  const [detailsYearFilter, setDetailsYearFilter] = useState('all')
  const [detailsMonthFilter, setDetailsMonthFilter] = useState('all')

  const breakdownSectionRef = useRef<HTMLDivElement>(null)
  const selectedProjectRowRef = useRef<HTMLTableRowElement>(null)
  const periodInitForUser = useRef<number | null>(null)

  const profileFilter: PeriodFilter = useMemo(() => {
    if (detailMode === 'fy')
      return { year: 'all', month: 'all', fy: detailFY, q: detailQ }
    return { year: detailYear, month: detailMonth, fy: 'all', q: 'all' }
  }, [detailMode, detailFY, detailQ, detailYear, detailMonth])

  const profileYearOptions = useMemo(() => {
    if (!data.length) return []
    return [...new Set(data.map((d) => d.year))].filter(Boolean).sort((a, b) => b - a)
  }, [data])

  const profileFYOptions = useMemo(() => {
    const opts = buildFYOptions(profileYearOptions, getCurrentFYStartYear())
    return opts.length ? opts : [{ value: '2025-26', label: 'FY 2025-26' }]
  }, [profileYearOptions])

  useEffect(() => {
    if (!userId || loading || !data.length) return
    if (periodInitForUser.current === userId) return
    periodInitForUser.current = userId
    setDetailMode('fy')
    setDetailFY(profileFYOptions[0]?.value ?? '2025-26')
    setDetailQ('all')
    setDetailYear('all')
    setDetailMonth('all')
    setActiveTab('overview')
    setSelectedProjectId(null)
    setDetailsYearFilter('all')
    setDetailsMonthFilter('all')
  }, [userId, loading, data, profileFYOptions])

  useEffect(() => {
    if (detailMode === 'fy' && profileFYOptions.length > 0) {
      const valid = profileFYOptions.some((o) => o.value === detailFY)
      if (!valid) setDetailFY(profileFYOptions[0].value)
    }
  }, [detailMode, detailFY, profileFYOptions])

  useEffect(() => {
    if (selectedProjectId != null && breakdownSectionRef.current) {
      breakdownSectionRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      })
    }
  }, [selectedProjectId])

  const userStats = useMemo(
    () => computeEmployeeUserStats(data, profileFilter),
    [data, profileFilter],
  )

  const monthlyChartData = useMemo(
    () => buildEmployeeMonthlyChartData(userStats),
    [userStats],
  )

  const pieChartData = useMemo(() => {
    if (!userStats) return []
    return [
      { name: 'Billable', value: userStats.billableHours, color: '#22c55e' },
      {
        name: 'Non-Billable',
        value: userStats.nonBillableHours,
        color: '#ef4444',
      },
    ]
  }, [userStats])

  const chartAxis = useMemo(
    () => ({
      grid: '#e5e7eb',
      tick: '#6b7280',
      tooltipBg: '#fff',
      tooltipBorder: '#e5e7eb',
    }),
    [],
  )

  const exportDetailsRows = useMemo(() => {
    if (!userStats?.monthlyData) return []
    return getMonthlyDetailsExportRows(
      userStats.monthlyData,
      detailsYearFilter,
      detailsMonthFilter,
    )
  }, [userStats?.monthlyData, detailsYearFilter, detailsMonthFilter])

  const userSlug =
    userStats?.userInfo?.full_name ||
    userStats?.userInfo?.username ||
    'user'

  const onExportProjectsCsv = () => {
    if (!userStats) return
    const rows = getProjectsExportRows(userStats)
    exportRowsToCsv(`user_projects_${userSlug}`, rows)
  }

  const onExportProjectsExcel = () => {
    if (!userStats) return
    const rows = getProjectsExportRows(userStats)
    exportRowsToExcel(`user_projects_${userSlug}`, 'Projects', rows)
  }

  const onExportDetailsCsv = () => {
    exportRowsToCsv(`monthly_details_${userSlug}`, exportDetailsRows)
  }

  const onExportDetailsExcel = () => {
    exportRowsToExcel(
      `monthly_details_${userSlug}`,
      'Monthly Details',
      exportDetailsRows,
    )
  }

  const first = data[0]
  const headerTitle =
    userStats?.userInfo?.full_name ||
    first?.full_name ||
    first?.username ||
    `Employee ${userId}`
  const headerEmail =
    userStats?.userInfo?.email ?? first?.email ?? null
  const headerUsername =
    userStats?.userInfo?.username ?? first?.username ?? null
  const headerStatusActive =
    userStats != null
      ? userStats.userInfo.is_active === 1 ||
        userStats.userInfo.is_active === true
      : first?.user_is_active === 1 || first?.user_is_active === true

  return {
    userId,
    onBack,
    loading,
    error,
    rawData: data,
    userStats,
    headerTitle,
    headerEmail,
    headerUsername,
    headerStatusActive,
    detailMode,
    setDetailMode,
    detailFY,
    setDetailFY,
    detailQ,
    setDetailQ,
    detailYear,
    setDetailYear,
    detailMonth,
    setDetailMonth,
    profileFYOptions,
    profileYearOptions,
    activeTab,
    setActiveTab,
    selectedProjectId,
    setSelectedProjectId,
    detailsYearFilter,
    setDetailsYearFilter,
    detailsMonthFilter,
    setDetailsMonthFilter,
    breakdownSectionRef,
    selectedProjectRowRef,
    monthlyChartData,
    pieChartData,
    chartAxis,
    exportDetailsRows,
    gidLate,
    gidUtil,
    onExportProjectsCsv,
    onExportProjectsExcel,
    onExportDetailsCsv,
    onExportDetailsExcel,
  }
}
